// View Manager - Handles different view modes on Edit page
import { PhotoRender } from './photo_render.js';
import { getRenderParams } from './slider_mappings.js';
import { PhotoRenderStatus } from './photo_render_status.js';
import { calculateImageBounds, getRenderedDimensions } from './image_dimensions.js';

export class ViewManager {
  constructor(stateManager, photoManager, sliderManager) {
    this.stateManager = stateManager;
    this.photoManager = photoManager;
    this.sliderManager = sliderManager;
    this.editorState = stateManager.getEditorState();
    
    // Elements
    this.normalView = null;
    this.sideBySideView = null;
    this.splitView = null;
    this.viewModeButtons = null;
    
    // Split view state
    this.splitSlider = null;
    this.splitContainer = null;
    this.splitOverlay = null;
    this.isDragging = false;
    this.currentPhoto = null;
    
    // Single PhotoRender instance for main view
    this.mainPhotoRender = null;
    this.mainPhotoContainer = null;
    this.currentRenderedImageUrl = null; // Track current rendered image
    
    // Single PhotoRenderStatus instance that persists across view mode changes
    this.photoRenderStatus = null;
    
    // Debounce timer for renders on slider changes
    this.renderDebounceTimer = null;
    this.renderDebounceDelay = 500; // ms
    
    this.setupElements();
    this.setupStateListeners();
    this.setupEventListeners();
    this.handleInitialState();
  }
  
  /**
   * Handle initial state after listeners are set up
   * This ensures buttons show correctly when the state is already 'edit' on load
   */
  handleInitialState() {
    // Defer this until after photos are loaded
    // Use setTimeout to run after the current call stack completes
    setTimeout(() => {
      const state = this.editorState.get();
      
      // Handle initial page state
      if (state.currentPage === 'edit') {
        this.viewModeButtons?.classList.remove('hidden');
        this.viewModeButtons?.classList.add('flex');
        this.applyViewMode(state.viewMode);
        
        // Create PhotoRender if we have an active photo
        if (state.activePhotoId) {
          const photo = this.photoManager.getPhotoById(state.activePhotoId);
          if (photo) {
            this.createMainPhotoRender(photo);
          }
        }
      }
    }, 100); // Small delay to ensure photos are loaded
  }
  
  setupElements() {
    this.normalView = document.getElementById('normal-view');
    this.sideBySideView = document.getElementById('sidebyside-view');
    this.splitView = document.getElementById('split-view');
    this.viewModeButtons = document.getElementById('view-mode-buttons');
    
    // Split view elements
    this.splitSlider = document.getElementById('split-slider');
    this.splitContainer = document.getElementById('split-container');
    this.splitOverlay = document.getElementById('split-overlay');
    
    // Split view image bounds (calculated dynamically)
    this.splitImageBounds = null;
    this.resizeObserver = null;
  }
  
  setupStateListeners() {
    // Listen for page changes to show/hide view mode buttons
    this.editorState.addEventListener('currentPage', (e) => {
      const page = e.detail.value;
      if (page === 'edit') {
        this.viewModeButtons?.classList.remove('hidden');
        this.viewModeButtons?.classList.add('flex');
        // Apply current view mode
        this.applyViewMode(this.editorState.get('viewMode'));
        
        // Create PhotoRender instance when entering edit page
        const state = this.editorState.get();
        if (state.activePhotoId) {
          const photo = this.photoManager.getPhotoById(state.activePhotoId);
          if (photo) {
            this.createMainPhotoRender(photo);
          }
        }
      } else {
        this.viewModeButtons?.classList.add('hidden');
        this.viewModeButtons?.classList.remove('flex');
        
        // Destroy PhotoRender instance when leaving edit page
        this.destroyMainPhotoRender();
      }
    });
    
    // Listen for view mode changes
    this.editorState.addEventListener('viewMode', (e) => {
      const viewMode = e.detail.value;
      this.applyViewMode(viewMode);
    });
    
    // Listen for photo changes
    this.editorState.addEventListener('photoChanged', (e) => {
      const { photo, photoId } = e.detail;
      if (photo) {
        this.currentPhoto = photo;
        
        // Clear rendered image URL when switching to a different photo
        if (this.mainPhotoRender && this.mainPhotoRender.photoId !== photoId) {
          this.currentRenderedImageUrl = null;
        }
        
        this.updateViewImages(photo);
        
        // Update PhotoRender with new photo when on edit page
        const state = this.editorState.get();
        if (state.currentPage === 'edit') {
          if (this.mainPhotoRender && this.mainPhotoRender.photoId === photoId) {
            // Same photo, just update params
            this.updateMainPhotoRenderParams(photo);
          } else {
            // Different photo, recreate PhotoRender
            this.createMainPhotoRender(photo);
          }
        }
      }
    });
    
    // Listen for photo updates (e.g., when thumbnails finish uploading)
    // We need to listen to all folder states since we don't know which folder the current photo is in
    const folderStateManager = this.stateManager.getFolderStateManager();
    const folders = folderStateManager.getAllFolders();
    folders.forEach(folder => {
      const folderState = folderStateManager.getFolderState(folder.id);
      if (folderState) {
        folderState.addEventListener('photoUpdated', (e) => {
          const updatedPhoto = e.detail.photo;
          // If this is the currently displayed photo, update the view
          if (this.currentPhoto && this.currentPhoto.id === updatedPhoto.id) {
            console.log(`[ViewManager] Photo ${updatedPhoto.id} updated, refreshing view with new URLs`);
            this.currentPhoto = updatedPhoto;
            this.updateViewImages(updatedPhoto, this.currentRenderedImageUrl);
          }
        });
      }
    });
    
    // Listen for slider changes - but DON'T render while dragging
    this.editorState.addEventListener('sliderChanged', (e) => {
      // Don't render while slider is being dragged
      if (this.editorState.isSliderBeingDragged()) {
        return;
      }
      
      const state = this.editorState.get();
      if (state.currentPage === 'edit' && state.activePhotoId) {
        const photo = this.photoManager.getPhotoById(state.activePhotoId);
        if (photo) {
          // Debounced render on slider change
          this.debouncedRender(photo);
        }
      }
    });
    
    // Listen for slider release to trigger render
    this.editorState.addEventListener('sliderReleased', (e) => {
      const state = this.editorState.get();
      if (state.currentPage === 'edit' && state.activePhotoId) {
        const photo = this.photoManager.getPhotoById(state.activePhotoId);
        if (photo) {
          // Immediate render on slider release (no debounce)
          this.updateMainPhotoRenderParams(photo);
        }
      }
    });
    
    // Listen for render settings changes (inferenceSteps, seed)
    // Don't render while dragging
    this.editorState.addEventListener('inferenceSteps', (e) => {
      // Don't render while slider is being dragged
      if (this.editorState.isSliderBeingDragged()) {
        return;
      }
      
      const state = this.editorState.get();
      if (state.currentPage === 'edit' && state.activePhotoId) {
        const photo = this.photoManager.getPhotoById(state.activePhotoId);
        if (photo) {
          this.debouncedRender(photo);
        }
      }
    });
    
    this.editorState.addEventListener('seed', (e) => {
      const state = this.editorState.get();
      if (state.currentPage === 'edit' && state.activePhotoId) {
        const photo = this.photoManager.getPhotoById(state.activePhotoId);
        if (photo) {
          this.debouncedRender(photo);
        }
      }
    });
    
    // Listen for fullResInferenceSteps changes
    this.editorState.addEventListener('fullResInferenceSteps', (e) => {
      // Don't render while slider is being dragged
      if (this.editorState.isSliderBeingDragged()) {
        return;
      }
      
      const state = this.editorState.get();
      if (state.currentPage === 'edit' && state.activePhotoId) {
        const photo = this.photoManager.getPhotoById(state.activePhotoId);
        if (photo) {
          // Use debounced render to prevent rapid successive renders
          this.debouncedRender(photo);
        }
      }
    });
    
    // Listen for fullResolutionSize changes
    this.editorState.addEventListener('fullResolutionSize', (e) => {
      // Don't render while slider is being dragged
      if (this.editorState.isSliderBeingDragged()) {
        return;
      }
      
      const state = this.editorState.get();
      if (state.currentPage === 'edit' && state.activePhotoId) {
        const photo = this.photoManager.getPhotoById(state.activePhotoId);
        if (photo) {
          // Resolution change requires recreating the PhotoRender instance
          this.createMainPhotoRender(photo);
        }
      }
    });
    
    // Listen for baseShift changes
    this.editorState.addEventListener('baseShift', (e) => {
      // Don't render while slider is being dragged
      if (this.editorState.isSliderBeingDragged()) {
        return;
      }
      
      const state = this.editorState.get();
      if (state.currentPage === 'edit' && state.activePhotoId) {
        const photo = this.photoManager.getPhotoById(state.activePhotoId);
        if (photo) {
          // Use debounced render to prevent rapid successive renders
          this.debouncedRender(photo);
        }
      }
    });
    
    // Listen for maxShift changes
    this.editorState.addEventListener('maxShift', (e) => {
      // Don't render while slider is being dragged
      if (this.editorState.isSliderBeingDragged()) {
        return;
      }
      
      const state = this.editorState.get();
      if (state.currentPage === 'edit' && state.activePhotoId) {
        const photo = this.photoManager.getPhotoById(state.activePhotoId);
        if (photo) {
          // Use debounced render to prevent rapid successive renders
          this.debouncedRender(photo);
        }
      }
    });
    
    // Listen for style changes (from slider_manager)
    document.addEventListener('photoSettingsChanged', (e) => {
      const { photo, setting } = e.detail;
      const state = this.editorState.get();
      
      if (state.currentPage === 'edit' && state.activePhotoId === photo.id && setting === 'style') {
        // Style changed, trigger immediate re-render
        this.updateMainPhotoRenderParams(photo);
      }
    });
  }
  
  setupEventListeners() {
    // View mode button clicks
    document.querySelectorAll('.view-mode-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const mode = btn.dataset.mode;
        this.editorState.set({ viewMode: mode });
        this.stateManager.saveState();
      });
    });
    
    // Split view slider and image dragging
    if (this.splitSlider && this.splitContainer) {
      // Allow dragging on the slider itself
      this.splitSlider.addEventListener('mousedown', this.handleSplitStart.bind(this));
      this.splitSlider.addEventListener('touchstart', this.handleSplitStart.bind(this));
      
      // Allow dragging on the images to move the slider
      const splitImageWrapper = document.getElementById('split-image-wrapper');
      if (splitImageWrapper) {
        splitImageWrapper.addEventListener('mousedown', this.handleSplitStart.bind(this));
        splitImageWrapper.addEventListener('touchstart', this.handleSplitStart.bind(this));
      }
      
      // Global move and end listeners
      document.addEventListener('mousemove', this.handleSplitMove.bind(this));
      document.addEventListener('mouseup', this.handleSplitEnd.bind(this));
      document.addEventListener('touchmove', this.handleSplitMove.bind(this));
      document.addEventListener('touchend', this.handleSplitEnd.bind(this));
    }
    
    // Setup ResizeObserver for split view image bounds
    this.setupSplitViewResizeObserver();
  }
  
  applyViewMode(mode) {
    // Hide all views
    this.normalView?.classList.add('hidden');
    this.sideBySideView?.classList.add('hidden');
    this.splitView?.classList.add('hidden');
    
    // Show selected view
    switch (mode) {
      case 'normal':
        this.normalView?.classList.remove('hidden');
        // Move PhotoRenderStatus to normal view image wrapper
        if (this.photoRenderStatus) {
          const editImageWrapper = document.getElementById('edit-image-wrapper');
          if (editImageWrapper) {
            this.movePhotoRenderStatusToView(editImageWrapper);
            this.photoRenderStatus.resetPosition();
          }
        }
        break;
      case 'sidebyside':
        this.sideBySideView?.classList.remove('hidden');
        // Move PhotoRenderStatus to sidebyside edited image wrapper
        if (this.photoRenderStatus) {
          const sideBySideEditedWrapper = document.getElementById('sidebyside-edited-wrapper');
          if (sideBySideEditedWrapper) {
            this.movePhotoRenderStatusToView(sideBySideEditedWrapper);
            this.photoRenderStatus.resetPosition();
          }
        }
        break;
      case 'split':
        this.splitView?.classList.remove('hidden');
        // Move PhotoRenderStatus to split view image wrapper
        if (this.photoRenderStatus) {
          const splitImageWrapper = document.getElementById('split-image-wrapper');
          if (splitImageWrapper) {
            this.movePhotoRenderStatusToView(splitImageWrapper);
            // Set split view positioning (uses CSS variable)
            this.photoRenderStatus.setSplitViewPosition(50);
          }
        }
        break;
    }
    
    // Update button states
    document.querySelectorAll('.view-mode-btn').forEach(btn => {
      if (btn.dataset.mode === mode) {
        btn.classList.add('bg-gray-700', 'text-white');
      } else {
        btn.classList.remove('bg-gray-700', 'text-white');
        btn.classList.add('text-gray-400');
      }
    });
    
    // Update images for current photo
    if (this.currentPhoto) {
      // Update images, passing the cached render URL if available to avoid flash
      this.updateViewImages(this.currentPhoto, this.currentRenderedImageUrl);
    }
  }
  
  updateViewImages(photo, renderedUrl = null) {
    // Get caller info for debugging
    const stack = new Error().stack;
    const caller = stack.split('\n')[2]?.trim() || 'unknown';
    
    console.log(`[ViewManager.updateViewImages] Called for photo ${photo.id}`, {
      renderedUrl: renderedUrl ? 'provided' : 'null',
      caller,
      viewMode: this.editorState.get('viewMode')
    });
    
    // Get the current full resolution size from state to match thumbnail size to render size
    const state = this.editorState.get();
    const resolutionSize = state.fullResolutionSize || 1024;
    
    // Select thumbnail that matches the render resolution
    // We only have thumb_1024_url available (browser-resized), use it for all resolutions
    const originalUrl = photo.thumb_1024_url;
    
    // Check if 1024px image is available
    if (!originalUrl) {
      console.log(`[ViewManager] Photo ${photo.id} thumb_1024_url not yet available, waiting for upload...`);
      // Image not ready yet - show empty state or keep current view
      // The photoUpdated event will call this again when ready
      return;
    }
    
    // For normal view - use rendered image if available, otherwise original
    const editImage = document.getElementById('edit-image');
    if (editImage) {
      const newSrc = renderedUrl || originalUrl;
      console.log(`[ViewManager.updateViewImages] Setting edit-image src to ${renderedUrl ? 'rendered' : 'original'}`);
      
      // Hide image while loading, show when loaded
      if (editImage.src !== newSrc) {
        editImage.style.visibility = 'hidden';
        
        editImage.onload = () => {
          editImage.style.visibility = 'visible';
          this.calculateImageBoundsForCurrentView();
        };
        editImage.onerror = () => {
          // Keep hidden on error
        };
        editImage.src = newSrc;
      } else {
        // Image already loaded
        editImage.style.visibility = 'visible';
      }
      
      // Calculate bounds immediately if we have photo dimensions
      // This avoids waiting for image load
      this.calculateImageBoundsForCurrentView();
      
      editImage.classList.remove('hidden');
      
      const emptyState = document.getElementById('edit-empty-state');
      if (emptyState) {
        emptyState.classList.add('hidden');
      }
    }
    
    // For side-by-side view - always show original on left, use rendered on right if available
    const sideBySideOriginal = document.getElementById('sidebyside-original');
    const sideBySideEdited = document.getElementById('sidebyside-edited');
    if (sideBySideOriginal) {
      if (sideBySideOriginal.src !== originalUrl) {
        sideBySideOriginal.style.visibility = 'hidden';
        
        sideBySideOriginal.onload = () => {
          sideBySideOriginal.style.visibility = 'visible';
        };
        sideBySideOriginal.onerror = () => {
          // Keep hidden on error
        };
        sideBySideOriginal.src = originalUrl;
      } else {
        sideBySideOriginal.style.visibility = 'visible';
      }
    }
    if (sideBySideEdited) {
      const newSrc = renderedUrl || originalUrl;
      console.log(`[ViewManager.updateViewImages] Setting sidebyside-edited src to ${renderedUrl ? 'rendered' : 'original'}`);
      
      // Hide image while loading, show when loaded
      if (sideBySideEdited.src !== newSrc) {
        sideBySideEdited.style.visibility = 'hidden';
        
        sideBySideEdited.onload = () => {
          sideBySideEdited.style.visibility = 'visible';
          this.calculateImageBoundsForCurrentView();
        };
        sideBySideEdited.onerror = () => {
          // Keep hidden on error
        };
        sideBySideEdited.src = newSrc;
      } else {
        sideBySideEdited.style.visibility = 'visible';
      }
      
      // Calculate bounds immediately if we have photo dimensions
      this.calculateImageBoundsForCurrentView();
    }
    
    // For split view - edited (base layer, right side), original (overlay, left side)
    const splitOriginal = document.getElementById('split-original');
    const splitEdited = document.getElementById('split-edited');
    
    // Set edited image to rendered if available, otherwise original
    if (splitEdited) {
      const newSrc = renderedUrl || originalUrl;
      console.log(`[ViewManager.updateViewImages] Setting split-edited src to ${renderedUrl ? 'rendered' : 'original'}`, {
        currentSrc: splitEdited.src,
        newSrc,
        renderedUrl,
        originalUrl
      });
      
      // Hide image while loading, show when loaded
      if (splitEdited.src !== newSrc) {
        splitEdited.style.visibility = 'hidden';
        
        splitEdited.onload = () => {
          splitEdited.style.visibility = 'visible';
          this.calculateSplitImageBounds();
          this.updateSliderVerticalBounds();
        };
        splitEdited.onerror = () => {
          // Keep hidden on error
        };
        splitEdited.src = newSrc;
      } else {
        splitEdited.style.visibility = 'visible';
      }
      
      // Calculate bounds immediately if we have photo dimensions
      this.calculateSplitImageBounds();
      this.updateSliderVerticalBounds();
    }
    
    // Original overlay always shows the original image
    if (splitOriginal) {
      console.log(`[ViewManager.updateViewImages] Setting split-original src to original`);
      
      if (splitOriginal.src !== originalUrl) {
        splitOriginal.style.visibility = 'hidden';
        
        splitOriginal.onload = () => {
          splitOriginal.style.visibility = 'visible';
        };
        splitOriginal.onerror = () => {
          // Keep hidden on error
        };
        splitOriginal.src = originalUrl;
      } else {
        splitOriginal.style.visibility = 'visible';
      }
    }
  }
  
  /**
   * Update all view images with the rendered image URL
   */
  updateRenderedImage(imageUrl, isFinal) {
    // Get caller info for debugging
    const stack = new Error().stack;
    const caller = stack.split('\n')[2]?.trim() || 'unknown';
    
    console.log(`[ViewManager.updateRenderedImage] Called with ${isFinal ? 'final' : 'progress'} image`, {
      imageUrl: imageUrl.substring(0, 50) + '...',
      caller,
      viewMode: this.editorState.get('viewMode')
    });
    
    // Store the current rendered image URL
    this.currentRenderedImageUrl = imageUrl;
    
    const state = this.editorState.get();
    const viewMode = state.viewMode;
    
    if (viewMode === 'normal') {
      const editImage = document.getElementById('edit-image');
      if (editImage) {
        console.log(`[ViewManager.updateRenderedImage] Updating edit-image to rendered URL`);
        editImage.onload = () => {
          this.calculateImageBoundsForCurrentView();
        };
        editImage.src = imageUrl;
        editImage.classList.remove('hidden');
        
        // Calculate bounds immediately if we have photo dimensions
        this.calculateImageBoundsForCurrentView();
        
        const emptyState = document.getElementById('edit-empty-state');
        if (emptyState) {
          emptyState.classList.add('hidden');
        }
      }
    } else if (viewMode === 'split') {
      // Update the base layer (edited image) with the new render
      const splitEdited = document.getElementById('split-edited');
      if (splitEdited) {
        console.log(`[ViewManager.updateRenderedImage] Updating split-edited to rendered URL`, {
          currentSrc: splitEdited.src.substring(0, 50) + '...',
          newSrc: imageUrl.substring(0, 50) + '...'
        });
        splitEdited.onload = () => {
          this.calculateSplitImageBounds();
          this.updateSliderVerticalBounds();
        };
        splitEdited.src = imageUrl;
        
        // Calculate bounds immediately if we have photo dimensions
        this.calculateSplitImageBounds();
        this.updateSliderVerticalBounds();
      }
    } else if (viewMode === 'sidebyside') {
      const sideBySideEdited = document.getElementById('sidebyside-edited');
      if (sideBySideEdited) {
        console.log(`[ViewManager.updateRenderedImage] Updating sidebyside-edited to rendered URL`);
        sideBySideEdited.onload = () => {
          this.calculateImageBoundsForCurrentView();
        };
        sideBySideEdited.src = imageUrl;
        
        // Calculate bounds immediately if we have photo dimensions
        this.calculateImageBoundsForCurrentView();
      }
    }
  }
  
  /**
   * Calculate image bounds for the current view mode
   * This is a helper that delegates to the appropriate view-specific calculation
   */
  calculateImageBoundsForCurrentView() {
    const state = this.editorState.get();
    const viewMode = state.viewMode;
    
    if (viewMode === 'split') {
      this.calculateSplitImageBounds();
      this.updateSliderVerticalBounds();
    } else if (viewMode === 'normal') {
      this.calculateNormalImageBounds();
    } else if (viewMode === 'sidebyside') {
      this.calculateSideBySideImageBounds();
    }
  }
  
  /**
   * Calculate the actual rendered bounds of the normal view image
   */
  calculateNormalImageBounds() {
    const editImage = document.getElementById('edit-image');
    const imageWrapper = document.getElementById('edit-image-wrapper');
    
    if (!editImage || !imageWrapper) {
      console.warn('calculateNormalImageBounds: Missing elements', { editImage: !!editImage, imageWrapper: !!imageWrapper });
      return;
    }
    
    const state = this.editorState.get();
    const photo = this.currentPhoto;
    
    // If we have photo dimensions, calculate bounds immediately
    if (photo && photo.original_width && photo.original_height) {
      const resolution = state.fullResolutionSize || 1024;
      const bounds = calculateImageBounds(photo, resolution, imageWrapper);
      
      if (bounds && bounds.width > 0 && bounds.height > 0) {
        this.splitImageBounds = {
          left: bounds.left,
          right: bounds.left + bounds.width,
          top: bounds.top,
          bottom: bounds.top + bounds.height,
          width: bounds.width,
          height: bounds.height,
          wrapperLeft: imageWrapper.getBoundingClientRect().left
        };
        
        console.log('calculateNormalImageBounds: Calculated from dimensions', this.splitImageBounds);
        this.updatePhotoRenderStatusBounds();
        return;
      }
    }
    
    // Fallback to using naturalWidth if image is loaded but dimensions not available
    if (editImage.complete && editImage.naturalWidth) {
      const wrapperRect = imageWrapper.getBoundingClientRect();
      const imgRect = editImage.getBoundingClientRect();
      
      const offsetX = imgRect.left - wrapperRect.left;
      const offsetY = imgRect.top - wrapperRect.top;
      const width = imgRect.width;
      const height = imgRect.height;
      
      this.splitImageBounds = {
        left: offsetX,
        right: offsetX + width,
        top: offsetY,
        bottom: offsetY + height,
        width: width,
        height: height,
        wrapperLeft: wrapperRect.left
      };
      
      console.log('calculateNormalImageBounds: Calculated from loaded image', this.splitImageBounds);
      this.updatePhotoRenderStatusBounds();
    } else {
      console.warn('calculateNormalImageBounds: Image not loaded yet', {
        complete: editImage.complete,
        naturalWidth: editImage.naturalWidth,
        hasDimensions: !!(photo && photo.original_width && photo.original_height)
      });
    }
  }
  
  /**
   * Calculate the actual rendered bounds of the side-by-side edited image
   */
  calculateSideBySideImageBounds() {
    const sideBySideEdited = document.getElementById('sidebyside-edited');
    const imageWrapper = document.getElementById('sidebyside-edited-wrapper');
    
    if (!sideBySideEdited || !imageWrapper) {
      return;
    }
    
    const state = this.editorState.get();
    const photo = this.currentPhoto;
    
    // If we have photo dimensions, calculate bounds immediately
    if (photo && photo.original_width && photo.original_height) {
      const resolution = state.fullResolutionSize || 1024;
      const bounds = calculateImageBounds(photo, resolution, imageWrapper);
      
      if (bounds) {
        this.splitImageBounds = {
          left: bounds.left,
          right: bounds.left + bounds.width,
          top: bounds.top,
          bottom: bounds.top + bounds.height,
          width: bounds.width,
          height: bounds.height,
          wrapperLeft: imageWrapper.getBoundingClientRect().left
        };
        
        this.updatePhotoRenderStatusBounds();
        return;
      }
    }
    
    // Fallback to using naturalWidth if image is loaded but dimensions not available
    if (sideBySideEdited.complete && sideBySideEdited.naturalWidth) {
      const wrapperRect = imageWrapper.getBoundingClientRect();
      const imgRect = sideBySideEdited.getBoundingClientRect();
      
      const offsetX = imgRect.left - wrapperRect.left;
      const offsetY = imgRect.top - wrapperRect.top;
      const width = imgRect.width;
      const height = imgRect.height;
      
      this.splitImageBounds = {
        left: offsetX,
        right: offsetX + width,
        top: offsetY,
        bottom: offsetY + height,
        width: width,
        height: height,
        wrapperLeft: wrapperRect.left
      };
      
      this.updatePhotoRenderStatusBounds();
    }
  }
  
  /**
   * Calculate the actual rendered bounds of the split view image
   * Gets the actual rendered dimensions from the DOM element
   */
  calculateSplitImageBounds() {
    const splitEdited = document.getElementById('split-edited');
    const imageWrapper = document.getElementById('split-image-wrapper');
    
    if (!splitEdited || !imageWrapper) {
      this.splitImageBounds = null;
      return;
    }
    
    const state = this.editorState.get();
    const photo = this.currentPhoto;
    
    // If we have photo dimensions, calculate bounds immediately
    if (photo && photo.original_width && photo.original_height) {
      const resolution = state.fullResolutionSize || 1024;
      const bounds = calculateImageBounds(photo, resolution, imageWrapper);
      
      if (bounds) {
        this.splitImageBounds = {
          left: bounds.left,
          right: bounds.left + bounds.width,
          top: bounds.top,
          bottom: bounds.top + bounds.height,
          width: bounds.width,
          height: bounds.height,
          wrapperLeft: imageWrapper.getBoundingClientRect().left
        };
        
        this.updateSliderVerticalBounds();
        return;
      }
    }
    
    // Fallback to using naturalWidth if image is loaded but dimensions not available
    if (splitEdited.complete && splitEdited.naturalWidth) {
      const wrapperRect = imageWrapper.getBoundingClientRect();
      const imgRect = splitEdited.getBoundingClientRect();
      
      const offsetX = imgRect.left - wrapperRect.left;
      const offsetY = imgRect.top - wrapperRect.top;
      const width = imgRect.width;
      const height = imgRect.height;
      
      this.splitImageBounds = {
        left: offsetX,
        right: offsetX + width,
        top: offsetY,
        bottom: offsetY + height,
        width: width,
        height: height,
        wrapperLeft: wrapperRect.left
      };
      
      this.updateSliderVerticalBounds();
    } else {
      this.splitImageBounds = null;
    }
  }
  
  /**
   * Update the slider's vertical position to match image bounds
   * Note: With the new CSS-based layout, both horizontal and vertical positioning
   * are handled by CSS. The slider uses inset-y-0 for height and the --split-position
   * CSS variable for horizontal position. This method just ensures the CSS variable is updated.
   */
  updateSliderVerticalBounds() {
    // Just update the CSS variable - the slider positioning is now fully CSS-based
    this.updateSplitPositionCSSVariable();
    
    // Also update PhotoRenderStatus to match image bounds
    this.updatePhotoRenderStatusBounds();
  }
  
  /**
   * Update the CSS variable --split-position based on current state and image bounds
   * This ensures the overlay clip-path is positioned correctly after resizes
   */
  updateSplitPositionCSSVariable() {
    if (!this.splitView || !this.splitImageBounds) return;
    
    const imageWrapper = document.getElementById('split-image-wrapper');
    if (!imageWrapper) return;
    
    const state = this.editorState.get();
    const splitPosition = state.splitPosition !== undefined ? state.splitPosition : 50;
    
    // Calculate the pixel position within the image
    const { left, width } = this.splitImageBounds;
    const positionInImage = (splitPosition / 100) * width;
    const absolutePosition = left + positionInImage;
    
    // Convert to percentage of wrapper width
    const wrapperRect = imageWrapper.getBoundingClientRect();
    const wrapperPercentage = (absolutePosition / wrapperRect.width) * 100;
    
    // Update CSS variable
    this.splitView.style.setProperty('--split-position', `${wrapperPercentage}%`);
  }
  
  /**
   * Update PhotoRenderStatus to match image bounds (CSS-only positioning)
   */
  updatePhotoRenderStatusBounds() {
    if (!this.photoRenderStatus) {
      // console.warn('updatePhotoRenderStatusBounds: No photoRenderStatus');
      return;
    }
    
    if (!this.splitImageBounds) {
      console.warn('updatePhotoRenderStatusBounds: No splitImageBounds - hiding status');
      // Hide the status if we don't have valid bounds
      if (this.photoRenderStatus.overlay) {
        this.photoRenderStatus.overlay.style.display = 'none';
      }
      if (this.photoRenderStatus.progressBar) {
        this.photoRenderStatus.progressBar.style.display = 'none';
      }
      return;
    }
    
    const { left, top, width, height } = this.splitImageBounds;
    
    // Validate bounds
    if (!width || !height || width <= 0 || height <= 0) {
      console.warn('updatePhotoRenderStatusBounds: Invalid bounds', this.splitImageBounds);
      return;
    }
    
    const state = this.editorState.get();
    
    // Get the PhotoRenderStatus DOM elements
    const overlay = this.photoRenderStatus.overlay;
    const progressBar = this.photoRenderStatus.progressBar;
    
    // Get actual progress bar height (force layout if needed)
    let progressBarHeight = 48; // Default fallback
    if (progressBar) {
      // Temporarily show it to measure if hidden
      const wasHidden = progressBar.style.display === 'none';
      if (wasHidden) {
        progressBar.style.display = '';
        progressBar.style.visibility = 'hidden'; // Hidden but takes space
      }
      
      // Force layout calculation
      progressBarHeight = progressBar.offsetHeight;
      
      if (wasHidden) {
        progressBar.style.display = 'none';
        progressBar.style.visibility = '';
      }
    }
    
    if (state.viewMode === 'split') {
      // In split view, PhotoRenderStatus should cover only the right portion of the image
      const splitPosition = state.splitPosition !== undefined ? state.splitPosition : 50;
      
      if (overlay) {
        overlay.style.cssText = `
          position: absolute;
          left: calc(${left}px + ${width}px * ${splitPosition} / 100);
          top: ${top}px;
          right: auto;
          width: calc(${width}px * (100 - ${splitPosition}) / 100);
          height: ${height}px;
          background-color: rgba(0, 0, 0, 0.7);
          display: ${overlay.style.display};
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 10;
        `;
      }
      
      if (progressBar) {
        const currentDisplay = progressBar.style.display;
        progressBar.style.cssText = `
          position: absolute;
          left: calc(${left}px + ${width}px * ${splitPosition} / 100);
          bottom: auto;
          top: ${top + height - progressBarHeight}px;
          right: auto;
          width: calc(${width}px * (100 - ${splitPosition}) / 100);
          background-color: rgba(17, 24, 39, 0.8);
          padding: 0.75rem;
          z-index: 10;
          display: ${currentDisplay};
        `;
      }
    } else {
      // Normal and side-by-side views - cover entire image
      if (overlay) {
        overlay.style.cssText = `
          position: absolute;
          left: ${left}px;
          top: ${top}px;
          width: ${width}px;
          height: ${height}px;
          background-color: rgba(0, 0, 0, 0.7);
          display: ${overlay.style.display};
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 10;
        `;
      }
      
      if (progressBar) {
        const currentDisplay = progressBar.style.display;
        progressBar.style.cssText = `
          position: absolute;
          left: ${left}px;
          bottom: auto;
          top: ${top + height - progressBarHeight}px;
          width: ${width}px;
          background-color: rgba(17, 24, 39, 0.8);
          padding: 0.75rem;
          z-index: 10;
          display: ${currentDisplay};
        `;
      }
    }
  }
  
  /**
   * Setup ResizeObserver to recalculate image bounds on resize
   */
  setupSplitViewResizeObserver() {
    const imageWrapper = document.getElementById('split-image-wrapper');
    if (!imageWrapper) return;
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    
    this.resizeObserver = new ResizeObserver(() => {
      const state = this.editorState.get();
      if (state.currentPage === 'edit') {
        this.calculateImageBoundsForCurrentView();
      }
    });
    
    this.resizeObserver.observe(imageWrapper);
    
    // Also observe normal and sidebyside wrappers
    const editWrapper = document.getElementById('edit-image-wrapper');
    if (editWrapper) {
      this.resizeObserver.observe(editWrapper);
    }
    
    const sideBySideWrapper = document.getElementById('sidebyside-edited-wrapper');
    if (sideBySideWrapper) {
      this.resizeObserver.observe(sideBySideWrapper);
    }
  }
  
  handleSplitStart(e) {
    e.preventDefault();
    
    // Get mouse/touch position
    let clientX, clientY;
    if (e.type.includes('touch')) {
      clientX = e.touches[0]?.clientX;
      clientY = e.touches[0]?.clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    if (!clientX || !clientY) return;
    
    // Recalculate bounds at start of drag
    this.calculateSplitImageBounds();
    
    // Check if click/touch is within image bounds
    if (this.splitImageBounds) {
      const imageWrapper = document.getElementById('split-image-wrapper');
      if (!imageWrapper) return;
      
      const rect = imageWrapper.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      
      // Only allow dragging if within image bounds
      if (x < this.splitImageBounds.left || x > this.splitImageBounds.right ||
          y < this.splitImageBounds.top || y > this.splitImageBounds.bottom) {
        return; // Click is outside image bounds, don't start dragging
      }
    }
    
    this.isDragging = true;
    this.updateSliderVerticalBounds();
  }
  
  handleSplitMove(e) {
    if (!this.isDragging) return;
    
    e.preventDefault();
    
    // Get the image wrapper
    const imageWrapper = document.getElementById('split-image-wrapper');
    if (!imageWrapper) return;
    
    // Get mouse/touch position
    let clientX;
    if (e.type.includes('touch')) {
      clientX = e.touches[0]?.clientX;
    } else {
      clientX = e.clientX;
    }
    
    if (!clientX) return;
    
    const rect = imageWrapper.getBoundingClientRect();
    
    // If we have calculated image bounds, constrain to image area
    if (this.splitImageBounds) {
      const x = clientX - rect.left;
      
      // Clamp to image bounds
      const clampedX = Math.max(this.splitImageBounds.left, Math.min(this.splitImageBounds.right, x));
      
      // Calculate percentage relative to image width (0% = left edge of image, 100% = right edge of image)
      const { left, width } = this.splitImageBounds;
      const positionInImage = clampedX - left; // Position relative to image left edge
      const percentage = (positionInImage / width) * 100;
      
      // Update CSS variable (for overlay clip-path, which is relative to wrapper)
      if (this.splitView) {
        this.splitView.style.setProperty('--split-position', `${clampedX / rect.width * 100}%`);
      }
      
      // Save position to state (as percentage of image, not wrapper)
      this.editorState.set({ splitPosition: percentage });
      this.stateManager.saveState();
      
      // Update slider and PhotoRenderStatus positions directly
      this.updateSliderVerticalBounds();
    } else {
      // Fallback to full wrapper if bounds not calculated yet
      const x = clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      const clampedPercentage = Math.max(0, Math.min(100, percentage));
      
      if (this.splitView) {
        this.splitView.style.setProperty('--split-position', `${clampedPercentage}%`);
      }
      
      // Save position to state
      this.editorState.set({ splitPosition: clampedPercentage });
      this.stateManager.saveState();
      
      // Update slider and PhotoRenderStatus positions directly
      this.updateSliderVerticalBounds();
    }
  }
  
  handleSplitEnd() {
    this.isDragging = false;
  }
  
  /**
   * Get the appropriate container for the current view mode
   * Returns the image wrapper element so PhotoRenderStatus is constrained to image bounds
   */
  getCurrentViewContainer() {
    const state = this.editorState.get();
    const viewMode = state.viewMode;
    
    switch (viewMode) {
      case 'normal':
        // Use the image wrapper so status is constrained to image bounds
        return document.getElementById('edit-image-wrapper');
      case 'sidebyside':
        // Use the edited image wrapper (status only shows on edited side)
        return document.getElementById('sidebyside-edited-wrapper');
      case 'split':
        // Use the image wrapper for split view so status overlays the entire image area
        return document.getElementById('split-image-wrapper');
      default:
        return document.getElementById('edit-image-wrapper');
    }
  }
  
  /**
   * Move PhotoRenderStatus to a new container (reusing the same instance)
   */
  movePhotoRenderStatusToView(newContainer) {
    if (!this.photoRenderStatus || !newContainer) return;
    
    // Get the DOM elements from the current container
    const overlay = this.photoRenderStatus.overlay;
    const progressBar = this.photoRenderStatus.progressBar;
    
    // Only move if they exist and are not already in the target container
    if (overlay && overlay.parentElement !== newContainer) {
      newContainer.appendChild(overlay);
    }
    if (progressBar && progressBar.parentElement !== newContainer) {
      newContainer.appendChild(progressBar);
    }
    
    // Update the container reference
    this.photoRenderStatus.container = newContainer;
    
    // Ensure container has proper positioning
    const currentPosition = window.getComputedStyle(newContainer).position;
    if (currentPosition === 'static') {
      newContainer.style.position = 'relative';
    }
  }
  
  /**
   * Ensure PhotoRenderStatus exists and is in the right container
   */
  ensurePhotoRenderStatus() {
    const state = this.editorState.get();
    const viewMode = state.viewMode;
    
    if (!this.photoRenderStatus) {
      const container = this.getCurrentViewContainer();
      if (container) {
        this.photoRenderStatus = new PhotoRenderStatus(container, {
          text: 'Rendering...'
        });
        this.photoRenderStatus.create();
        
        // Apply split view positioning if in split mode (uses CSS variable)
        if (viewMode === 'split') {
          this.photoRenderStatus.setSplitViewPosition(50);
        }
      }
    } else {
      // Make sure it's in the right container for current view mode
      const currentContainer = this.getCurrentViewContainer();
      if (currentContainer && this.photoRenderStatus.container !== currentContainer) {
        this.movePhotoRenderStatusToView(currentContainer);
      }
      
      // Apply split view positioning if in split mode, reset otherwise
      if (viewMode === 'split') {
        this.photoRenderStatus.setSplitViewPosition(50);
      } else {
        this.photoRenderStatus.resetPosition();
      }
    }
    return this.photoRenderStatus;
  }

  /**
   * Create a PhotoRender instance for the main photo
   */
  createMainPhotoRender(photo) {
    // Check if photo has ready embeddings
    if (photo.processing_status !== 'completed') {
      console.log('Photo embeddings not ready yet');
      // Still show the thumbnail
      this.currentPhoto = photo; // Ensure currentPhoto is set
      this.updateViewImages(photo);
      return;
    }
    
    // Set currentPhoto FIRST before any other operations
    this.currentPhoto = photo;
    
    // Always update view images first to show originals
    this.updateViewImages(photo);
    
    // Destroy existing instance
    this.destroyMainPhotoRender();
    
    // Create hidden container for PhotoRender if it doesn't exist
    if (!this.mainPhotoContainer) {
      // Create a hidden container that's not visible but renders the photo
      this.mainPhotoContainer = document.createElement('div');
      this.mainPhotoContainer.id = 'main-photo-render-container';
      this.mainPhotoContainer.className = 'hidden';
      document.body.appendChild(this.mainPhotoContainer);
    }
    
    // Get current state for render settings
    const state = this.editorState.get();
    const renderParams = getRenderParams(photo, null, null, state.inferenceSteps, state.seed, state.sampleEvery);
    const selectedStyle = photo.render_style || 'hta';
    
    // Ensure PhotoRenderStatus exists and is in the right container
    const status = this.ensurePhotoRenderStatus();
    if (status) {
      status.resetProgress();
      status.showSpinner(100); // Show with 100ms delay
      
      // Calculate bounds immediately for the current image (which is already loaded)
      // Use setTimeout to ensure DOM has updated with new image
      setTimeout(() => {
        this.calculateImageBoundsForCurrentView();
      }, 0);
    }
    
    // Create PhotoRender instance with callbacks to update view images
    // Use fullResInferenceSteps for the main photo render
    this.mainPhotoRender = new PhotoRender(this.mainPhotoContainer, {
      photoId: photo.id,
      style: selectedStyle,
      resolution: state.fullResolutionSize,
      numInferenceSteps: state.fullResInferenceSteps,
      style_strength: renderParams.style_strength,
      controlScale: renderParams.controlScale,
      seed: renderParams.seed,
      baseShift: state.baseShift,
      maxShift: state.maxShift,
      sampleEvery: 1,
      jpgQuality: 95,
      onProgress: (imageUrl) => {
        const status = this.photoRenderStatus;
        if (!status) return;
        
        // Get current step and increment
        const currentStep = (status.currentStep || 0) + 1;
        
        // Get CURRENT full resolution inference steps from state, not captured value
        const currentState = this.editorState.get();
        const currentTotalSteps = currentState.fullResInferenceSteps || renderParams.numInferenceSteps;
        
        // Update progress bar with current total
        status.updateProgress(currentStep, currentTotalSteps);
        
        // On first frame, hide the spinner but show progress bar at bottom
        if (currentStep === 1 && status.isSpinnerVisible()) {
          status.hideSpinner();
        }
        
        this.updateRenderedImage(imageUrl, false);
      },
      onComplete: (imageUrl) => {
        const status = this.photoRenderStatus;
        if (status) {
          // Get CURRENT full resolution inference steps from state, not captured value
          const currentState = this.editorState.get();
          const currentTotalSteps = currentState.fullResInferenceSteps || renderParams.numInferenceSteps;
          
          // Final image - update to 100%
          status.updateProgress(currentTotalSteps, currentTotalSteps);
          
          // Fade out progress bar
          status.hideProgressBar();
          
          // Hide spinner overlay
          status.hideSpinner();
        }
        
        this.updateRenderedImage(imageUrl, true);
      },
      onError: (error) => {
        console.error('Main photo render error:', error);
        
        // Hide progress and show error
        const status = this.photoRenderStatus;
        if (status) {
          status.showError('Failed to render photo');
        }
      }
    });
  }
  
  /**
   * Update PhotoRender params without recreating
   */
  updateMainPhotoRenderParams(photo) {
    if (!this.mainPhotoRender) {
      this.createMainPhotoRender(photo);
      return;
    }
    
    // Check if photo has ready embeddings
    if (photo.processing_status !== 'completed') {
      console.log('Photo embeddings not ready yet');
      return;
    }
    
    // Get current state for render settings
    const state = this.editorState.get();
    const renderParams = getRenderParams(photo, null, null, state.inferenceSteps, state.seed, state.sampleEvery);
    const selectedStyle = photo.render_style || 'hta';
    
    // Ensure PhotoRenderStatus exists and reset it
    const status = this.ensurePhotoRenderStatus();
    if (status) {
      status.resetProgress();
      status.showSpinner(100); // Show with 100ms delay
      
      // Calculate bounds immediately for the current image (which is already loaded)
      this.calculateImageBoundsForCurrentView();
    }
    
    // Update params (this will cancel previous render and start new one)
    // Use fullResInferenceSteps for main photo render (not preview inferenceSteps)
    this.mainPhotoRender.updateParams({
      style: selectedStyle,
      resolution: state.fullResolutionSize,
      numInferenceSteps: state.fullResInferenceSteps,
      style_strength: renderParams.style_strength,
      controlScale: renderParams.controlScale,
      seed: renderParams.seed,
      baseShift: state.baseShift,
      maxShift: state.maxShift
    });
  }
  
  /**
   * Debounced render to prevent excessive re-renders on slider changes
   */
  debouncedRender(photo) {
    // Clear any pending render
    if (this.renderDebounceTimer) {
      clearTimeout(this.renderDebounceTimer);
    }
    
    // Schedule new render
    this.renderDebounceTimer = setTimeout(() => {
      this.renderDebounceTimer = null;
      this.updateMainPhotoRenderParams(photo);
    }, this.renderDebounceDelay);
  }
  
  /**
   * Destroy the PhotoRender instance
   */
  destroyMainPhotoRender() {
    if (this.mainPhotoRender) {
      this.mainPhotoRender.destroy();
      this.mainPhotoRender = null;
    }
    
    if (this.mainPhotoContainer) {
      this.mainPhotoContainer.remove();
      this.mainPhotoContainer = null;
    }
    
    // Clean up PhotoRenderStatus
    if (this.photoRenderStatus) {
      this.photoRenderStatus.destroy();
      this.photoRenderStatus = null;
    }
    
    // Clear rendered image URL
    this.currentRenderedImageUrl = null;
  }
  
  cleanup() {
    // Clear debounce timer
    if (this.renderDebounceTimer) {
      clearTimeout(this.renderDebounceTimer);
      this.renderDebounceTimer = null;
    }
    
    // Disconnect ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    
    // Destroy PhotoRender
    this.destroyMainPhotoRender();
  }
}
